import {
  Box,
  Button,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  Flex,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  RatingDot,
  useDisclosure,
} from '@televet/kibble-ui';
import { useTracking } from 'shared/providers/TrackingProvider';
import { Icon } from '@televet/kibble-ui/build/components/Icon';
import format from 'date-fns/format';
import cloneDeep from 'lodash-es/cloneDeep';
import React, { useMemo, useState } from 'react';
import Tooltip from 'shared/components/Tooltip';
import useClinicUser from 'shared/hooks/useClinicUser';
import {
  AutomationRunActionFragment,
  AutomationRunStatus,
  useUpdateAutomationRunActionMutation,
  WorkflowEvent,
} from 'shared/types/graphql';
import { ConversationEvents } from 'shared/providers/TrackingProvider/EventNames';

interface IAutomationRunButtonsPromptProps {
  automationRunAction: AutomationRunActionFragment | undefined;
  actionIndex: number;
  lastAutomatedActionIndex: number;
  isSurveyAutomation?: boolean;
}

interface IButton {
  body: string;
  clicked?: {
    clinicUserId?: string;
    petParentId?: string;
    clickedAt: string;
  };
}

const AutomationRunButtonsPrompt = ({
  automationRunAction,
  actionIndex,
  lastAutomatedActionIndex,
  isSurveyAutomation,
}: IAutomationRunButtonsPromptProps): JSX.Element => {
  const { clinicUser } = useClinicUser();
  const userId = clinicUser?.id || '';
  const { trackEvent } = useTracking();

  const { isOpen, onOpen, onClose } = useDisclosure();

  const [updateAutomationRunAction] = useUpdateAutomationRunActionMutation();

  const [buttonClicked, setButtonClicked] = useState<IButton | undefined>(undefined);

  const isHillsRegistrationWorkflow = useMemo(
    () => automationRunAction?.automationAction?.workflowEventSetting?.event === WorkflowEvent.InvoiceLineItemCreated,
    [automationRunAction?.automationAction?.workflowEventSetting?.event],
  );

  const areButtonsClickable = useMemo(() => {
    return (
      !isSurveyAutomation &&
      actionIndex === lastAutomatedActionIndex &&
      automationRunAction?.automationRun.status !== AutomationRunStatus.Completed &&
      !isHillsRegistrationWorkflow
    );
  }, [
    isSurveyAutomation,
    actionIndex,
    lastAutomatedActionIndex,
    automationRunAction?.automationRun.status,
    isHillsRegistrationWorkflow,
  ]);

  const handleButtonSubmit = (): void => {
    trackEvent(ConversationEvents.AutomationRunSubmit);
    const newButtons = cloneDeep(automationRunAction?.buttons);
    for (const button of newButtons) {
      if (buttonClicked?.body === button.body) {
        button.clicked = {
          clickedAt: new Date().toISOString(),
          clinicUserId: userId,
        };

        if (button.externalUrl) {
          window.open(button.externalUrl);
        }
      }
    }

    updateAutomationRunAction({
      variables: {
        where: { id: automationRunAction?.id },
        data: {
          buttons: newButtons,
        },
      },
    });
    onClose();
  };

  const sentTime = format(new Date(automationRunAction?.createdAt), 'h:mmaaa');
  return (
    <Box px={3}>
      <Flex mt="14px" alignItems="center" className="AutomationRunButtonsPrompt__ChildAutomationStart">
        {actionIndex > 0 && (
          <Box fontWeight="bold" mt={2} fontSize="md" className="AutomationRunButtonsPrompt__AutomationSent">
            Automation Sent{' '}
            <Box fontSize="12px" color="text.subtle" fontWeight="400" className="AutomationRunButtonsPrompt__SentTime">
              {sentTime}
            </Box>
          </Box>
        )}

        {automationRunAction?.order === 1 && actionIndex !== 0 && (
          <Tooltip content={automationRunAction?.automationAction?.workflowEventSetting?.name}>
            <Icon name="robot" size="sm" />
          </Tooltip>
        )}
      </Flex>

      <Box mt={1} whiteSpace="pre-wrap" className="AutomationRunButtonsPrompt__Body">
        {automationRunAction?.body}
      </Box>

      <Box
        className="AutomationRunButtonsPrompt__Answer"
        backgroundColor="--chakra-colors-background-primaryAlpha-hover"
        borderLeftWidth="4px"
        borderLeftColor="--chakra-colors-border-brand"
        p={3}
        mt={3}
        {...(!!isSurveyAutomation && { display: 'flex', flexWrap: 'wrap', justifyContent: 'center' })}
      >
        <Box mt={3} fontWeight="bold" className="CallToAction">
          {automationRunAction?.automationAction?.config.callToActionBody}
        </Box>
        <>
          {automationRunAction?.buttons.map((button: IButton, index: number) => {
            const isButtonClicked = !!button.clicked?.clickedAt;

            // don't allow clinic user to select rating value
            return isSurveyAutomation ? (
              <RatingDot
                isDisabled={true}
                key={index}
                number={parseInt(button.body)}
                selected={isButtonClicked}
                isInteractive={true}
              />
            ) : (
              <Button
                size="md"
                key={index}
                isDisabled={!areButtonsClickable}
                variant={isButtonClicked ? 'primary' : 'secondary'}
                mb={2}
                isFullWidth
                h="max-content"
                whiteSpace="break-spaces"
                onClick={(e): void => {
                  if (areButtonsClickable) {
                    if (!isButtonClicked) {
                      e.stopPropagation();
                      setButtonClicked(button);
                      onOpen();
                    }
                  }
                }}
              >
                {button.body}
              </Button>
            );
          })}
        </>
      </Box>

      <CModal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <CModalHeader>Submit Prompt</CModalHeader>
          <ModalCloseButton _focus={{ border: 'none' }} />
          <CModalBody>
            Submitting the prompt will continue the automation without input from the client.
            <br />
            <br />
            <span style={{ fontWeight: 'bold' }}>Are you sure you want to submit this prompt?</span>
          </CModalBody>

          <CModalFooter>
            <Flex flex={1} justifyContent="space-between" className="AutomationRunButtonsPrompt__ModalButtons">
              <Button variant="tertiary" onClick={onClose}>
                Cancel
              </Button>
              <Button onClick={handleButtonSubmit}>Submit Prompt</Button>
            </Flex>
          </CModalFooter>
        </ModalContent>
      </CModal>
    </Box>
  );
};

export default AutomationRunButtonsPrompt;
